import { FormApi } from "final-form";
import { Form } from "react-final-form";
import { Trans, useTranslation } from "react-i18next";
import { useValidationSchema } from "src/ui/utils/useValidationSchema";
import { makeStyles } from "tss-react/mui";

import { Box } from "@mui/material";
import { CustomAlert } from "@shared/CustomAlert/CustomAlert";
import { PrimaryButton } from "@shared/CustomButton";
import { ControlledCheckboxField } from "@shared/form/CheckboxField";
import PasswordField from "@shared/form/PasswordField";
import { TextField } from "@shared/form/TextField";
import { Modal } from "@shared/Modal/Modal";

import { setBwcCredentialsValidationSchema } from "./SetBwcCredentialsDialog.validation";

const useStyles = makeStyles()((theme) => ({
    form: { display: "flex", flexDirection: "column", gap: theme.spacing(2) },
    // The scope choice decides whether one device or a whole group is written to, so it gets more
    // room than the gap the modal puts between its children and does not read as a fourth field.
    groupScope: { marginTop: theme.spacing(2), marginBottom: theme.spacing(2) },
}));

export type BwcCredentialsFormValues = {
    bwcAccount: string;
    bwcSecret: string;
    bwcSecretRepeat: string;
    applyToGroup: boolean;
};

// A stable reference — react-final-form re-initializes the form whenever initialValues changes
// identity, which would wipe whatever the user has typed on every render.
const initialValues: Partial<BwcCredentialsFormValues> = { applyToGroup: false };

type Props = {
    open: boolean;
    loading: boolean;
    groupName: string;
    onClose: () => void;
    onSubmit: (values: BwcCredentialsFormValues) => Promise<void>;
};

export const SetBwcCredentialsDialog = ({ open, loading, groupName, onClose, onSubmit }: Props) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["sidepanels", "common"]);
    const validate = useValidationSchema(setBwcCredentialsValidationSchema);

    const handleSubmit = async (values: BwcCredentialsFormValues, form: FormApi<BwcCredentialsFormValues>) => {
        try {
            await onSubmit(values);
        } catch {
            // The caller shows its own error message. Catching here stops a rejected onSubmit
            // from becoming an unhandled rejection inside form.submit().
        } finally {
            // Never keep the credentials in form state, not even after a failed request.
            form.restart();
        }
    };

    return (
        <Form<BwcCredentialsFormValues>
            onSubmit={handleSubmit}
            validate={validate}
            initialValues={initialValues}
            render={({ handleSubmit: submitForm, form, submitting }) => (
                <Modal
                    open={open}
                    onClose={() => {
                        if (loading) {
                            return;
                        }
                        form.restart();
                        onClose();
                    }}
                    title={t("sidepanels:devicePanel.bwcTab.credentials.title")}
                    description={t("sidepanels:devicePanel.bwcTab.credentials.description")}
                    secondaryActionText={t("common:button.cancel")}
                    endButton={
                        <PrimaryButton onClick={form.submit} disabled={submitting || loading} loading={submitting || loading}>
                            {t("common:button.confirm")}
                        </PrimaryButton>
                    }
                >
                    {/*
                        These credentials belong to the device WebUI, not to the Hub. Chromium ignores
                        autocomplete="off" on credential fields and falls back to heuristics, so the field
                        names deliberately avoid "username"/"password" and the tokens below are the opt-outs
                        that do work: "new-password" for the passwords, an unrecognized token for the account.
                    */}
                    <form onSubmit={submitForm} className={classes.form}>
                        <TextField
                            name="bwcAccount"
                            label={t("sidepanels:devicePanel.bwcTab.credentials.usernameLabel")}
                            autoComplete="bwc-account-field"
                            fullWidth
                        />
                        <PasswordField
                            name="bwcSecret"
                            label={t("sidepanels:devicePanel.bwcTab.credentials.passwordLabel")}
                            autoComplete="new-password"
                            fullWidth
                        />
                        <PasswordField
                            name="bwcSecretRepeat"
                            label={t("sidepanels:devicePanel.bwcTab.credentials.passwordRepeatLabel")}
                            autoComplete="new-password"
                            fullWidth
                        />
                    </form>
                    <Box className={classes.groupScope}>
                        <ControlledCheckboxField
                            name="applyToGroup"
                            label={
                                <Trans
                                    ns="sidepanels"
                                    i18nKey="devicePanel.bwcTab.credentials.applyToGroupLabel"
                                    values={{ groupName }}
                                    /*
                                        The tag may not be called `link` or any other HTML void element:
                                        html-parse-stringify closes those immediately, which would leave the
                                        group name a sibling of the <strong> instead of its content.
                                    */
                                    components={{ groupName: <strong /> }}
                                />
                            }
                        />
                    </Box>
                    <CustomAlert severity="info">
                        <Trans
                            ns="sidepanels"
                            i18nKey="devicePanel.bwcTab.credentials.warning"
                            components={{ bold: <strong /> }}
                        />
                    </CustomAlert>
                </Modal>
            )}
        />
    );
};
